import React, { forwardRef, useEffect, useId, useImperativeHandle, useRef, useState } from 'react';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Underline from '@tiptap/extension-underline';
import Placeholder from '@tiptap/extension-placeholder';
import { EmojiHappyIcon, LinkIcon } from '@heroicons/react/outline';
import { BiListOl, BiListUl } from 'react-icons/bi';
import Button from './Button';
import { Input } from './Field';
import { toRichTextHtml } from './richTextUtils';
import { COMPACT_EMOJI_GROUPS } from './compactEmojiOptions';
import './CompactRichTextEditor.css';

const FORMATTING = [
  ['bold', 'Bold', 'toggleBold', <strong>B</strong>],
  ['italic', 'Italic', 'toggleItalic', <em>I</em>],
  ['underline', 'Underline', 'toggleUnderline', <u>U</u>],
  ['bulletList', 'Bulleted list', 'toggleBulletList', <BiListUl aria-hidden="true" />],
  ['orderedList', 'Numbered list', 'toggleOrderedList', <BiListOl aria-hidden="true" />],
];

/** Compact HTML editor. Saving and maximum-length admission belong to the caller. */
const CompactRichTextEditor = forwardRef(function CompactRichTextEditor({
  value = '', onChange, onBlur, ariaLabel = 'Rich text', id, placeholder = '', rows = 4, maxLength = 10000,
}, forwardedRef) {
  const generatedId = useId();
  const editorId = id || `compact-rich-text-${generatedId}`;
  const rootRef = useRef(null);
  const callbacksRef = useRef({ onChange, onBlur, placeholder });
  callbacksRef.current = { onChange, onBlur, placeholder };
  const receivedValueRef = useRef(value);
  const emittedValueRef = useRef(null);
  const blurTimerRef = useRef(null);
  const [focused, setFocused] = useState(false);
  const [picker, setPicker] = useState(null);
  const [emojiQuery, setEmojiQuery] = useState('');
  const [url, setUrl] = useState('');
  const [linkError, setLinkError] = useState('');
  const [draftHtml, setDraftHtml] = useState(null);
  const editor = useEditor({
    shouldRerenderOnTransaction: true,
    extensions: [
      StarterKit.configure({ heading: false, codeBlock: false, horizontalRule: false, trailingNode: false, link: false, underline: false }),
      Underline,
      Link.configure({ openOnClick: false, HTMLAttributes: { target: '_blank', rel: 'noopener noreferrer' } }),
      Placeholder.configure({ placeholder: () => callbacksRef.current.placeholder }),
    ],
    content: toRichTextHtml(value),
    editorProps: { attributes: { id: editorId, role: 'textbox', 'aria-multiline': 'true', 'aria-label': ariaLabel } },
    onUpdate: ({ editor: current }) => {
      const html = current.isEmpty ? '' : current.getHTML();
      emittedValueRef.current = html;
      setDraftHtml(html);
      callbacksRef.current.onChange?.(html);
    },
  });

  useEffect(() => {
    if (!editor || editor.isDestroyed || value === receivedValueRef.current) return;
    receivedValueRef.current = value;
    if (value === emittedValueRef.current) return;
    editor.commands.setContent(toRichTextHtml(value), { emitUpdate: false });
    emittedValueRef.current = null;
    setDraftHtml(null);
  }, [editor, value]);

  useEffect(() => () => clearTimeout(blurTimerRef.current), []);
  useImperativeHandle(forwardedRef, () => ({
    focus: () => { if (editor && !editor.isDestroyed) editor.commands.focus(undefined, { scrollIntoView: false }); },
    getHTML: () => editor && !editor.isDestroyed ? editor.getHTML() : '',
    getEditor: () => editor && !editor.isDestroyed ? editor : null,
  }), [editor]);

  const error = draftHtml !== null && draftHtml.length > maxLength
    ? `This note is too long to save. Remove ${draftHtml.length - maxLength} characters, including formatting.`
    : '';
  useEffect(() => {
    if (!editor || editor.isDestroyed) return;
    editor.view.dom.setAttribute('aria-invalid', error ? 'true' : 'false');
    if (error) editor.view.dom.setAttribute('aria-describedby', `${editorId}-error`);
    else editor.view.dom.removeAttribute('aria-describedby');
  }, [editor, editorId, error]);

  const focusEditor = () => editor.chain().focus(undefined, { scrollIntoView: false });
  const closePicker = () => { setPicker(null); focusEditor().run(); };
  const emojiGroups = COMPACT_EMOJI_GROUPS.map(group => ({ ...group, emojis: group.emojis.filter(([emoji, label]) =>
    `${group.label} ${label} ${emoji}`.toLowerCase().includes(emojiQuery.trim().toLowerCase())) })).filter(group => group.emojis.length);
  const applyLink = event => {
    event.preventDefault();
    const enteredUrl = url.trim();
    const href = /^(https?:\/\/|mailto:|tel:)/i.test(enteredUrl) ? enteredUrl : `https://${enteredUrl}`;
    if (enteredUrl && (/^[a-z][a-z\d+.-]*:/i.test(enteredUrl) && !/^(https?:\/\/|mailto:|tel:)/i.test(enteredUrl))) {
      setLinkError('Enter a web, email, or phone link.');
      return;
    }
    if (!enteredUrl) focusEditor().extendMarkRange('link').unsetLink().run();
    else if (editor.state.selection.empty && !editor.isActive('link')) {
      focusEditor().insertContent({ type: 'text', text: enteredUrl, marks: [{ type: 'link', attrs: { href } }] }).run();
    } else focusEditor().extendMarkRange('link').setLink({ href }).run();
    setPicker(null);
  };

  return <div ref={rootRef} className="bun-compact-rich-text" style={{ '--compact-editor-rows': Math.max(1, rows) }}
    onFocusCapture={() => { clearTimeout(blurTimerRef.current); setFocused(true); }}
    onBlurCapture={event => {
      if (rootRef.current?.contains(event.relatedTarget)) return;
      clearTimeout(blurTimerRef.current);
      blurTimerRef.current = setTimeout(() => {
        if (rootRef.current?.contains(document.activeElement)) return;
        setFocused(false);
        setPicker(null);
        callbacksRef.current.onBlur?.();
      }, 0);
    }}
    onKeyDown={event => {
      if (event.key === 'Escape' && picker) { event.preventDefault(); event.stopPropagation(); closePicker(); }
    }}>
    <EditorContent editor={editor} />
    {editor && focused && <div className="bun-compact-rich-text-toolbar" role="toolbar" aria-label={`${ariaLabel} formatting`}>
      {FORMATTING.map(([mark, label, command, icon]) => <Button key={mark} variant="ghost" size="sm" iconOnly shape="rounded"
        aria-label={label} title={label} selected={editor.isActive(mark)} onMouseDown={event => event.preventDefault()}
        onClick={() => focusEditor()[command]().run()}>{icon}</Button>)}
      <Button variant="ghost" size="sm" iconOnly shape="rounded" aria-label="Insert link" title="Insert link" selected={editor.isActive('link')}
        aria-expanded={picker === 'link'} aria-controls={`${editorId}-link`} onMouseDown={event => event.preventDefault()}
        onClick={() => { setUrl(editor.getAttributes('link').href || ''); setLinkError(''); setPicker(picker === 'link' ? null : 'link'); }}><LinkIcon aria-hidden="true" /></Button>
      <Button variant="ghost" size="sm" iconOnly shape="rounded" aria-label="Insert emoji" title="Insert emoji"
        aria-expanded={picker === 'emoji'} aria-controls={`${editorId}-emoji`} onMouseDown={event => event.preventDefault()}
        onClick={() => { setEmojiQuery(''); setPicker(picker === 'emoji' ? null : 'emoji'); }}><EmojiHappyIcon aria-hidden="true" /></Button>
    </div>}
    {picker === 'emoji' && <div id={`${editorId}-emoji`} className="bun-compact-rich-text-emojis" role="group" aria-label="Emojis">
      <Input type="search" size="sm" aria-label="Search emojis" placeholder="Search emojis…" value={emojiQuery} onChange={event => setEmojiQuery(event.target.value)} />
      <div className="bun-compact-rich-text-emoji-groups">
        {emojiGroups.map(group => <div key={group.label} role="group" aria-label={group.label}>
          <div className="px-1 py-1 text-bun-label font-medium text-bun-stone">{group.label}</div>
          <div className="bun-compact-rich-text-emoji-grid">
            {group.emojis.map(([emoji, label]) => <Button key={label} variant="ghost" size="sm" iconOnly shape="rounded" aria-label={label} title={label}
              onMouseDown={event => event.preventDefault()} onClick={() => { focusEditor().insertContent(emoji).run(); setPicker(null); }}>{emoji}</Button>)}
          </div>
        </div>)}
        {!emojiGroups.length && <p role="status" className="px-1 py-2 text-bun-meta text-bun-stone">No emojis found</p>}
      </div>
    </div>}
    {picker === 'link' && <div id={`${editorId}-link`} className="bun-compact-rich-text-link" role="group" aria-label="Insert link"
      onKeyDown={event => { if (event.key === 'Enter' && event.target.tagName === 'INPUT') applyLink(event); }}>
      <Input size="sm" aria-label="Link URL" value={url} onChange={event => { setUrl(event.target.value); setLinkError(''); }} error={Boolean(linkError)} />
      <Button size="sm" onClick={applyLink}>Apply</Button>
      {editor.isActive('link') && <Button variant="ghost" size="sm" onClick={() => { focusEditor().extendMarkRange('link').unsetLink().run(); setPicker(null); }}>Remove</Button>}
      <Button variant="ghost" size="sm" onClick={closePicker}>Cancel</Button>
      {linkError && <span role="alert" className="bun-compact-rich-text-error">{linkError}</span>}
    </div>}
    {error && <div id={`${editorId}-error`} role="alert" className="bun-compact-rich-text-error">{error}</div>}
  </div>;
});

export default CompactRichTextEditor;
